import { useEffect, useState } from "react";

/**
 * Singleton Google Maps loader. Also loads the `maps3d` library so the 3D
 * hook can `importLibrary('maps3d')` without a second script tag.
 */

declare global {
  interface Window {
    google?: any;
  }
}

let mapsLoadPromise: Promise<void> | null = null;

function loadGoogleMaps(): Promise<void> {
  if (mapsLoadPromise) return mapsLoadPromise;
  if (typeof window !== "undefined" && window.google?.maps) {
    return Promise.resolve();
  }
  mapsLoadPromise = new Promise((resolve, reject) => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
    if (!apiKey) {
      reject(new Error("Google Maps API key not configured (REACT_APP_GOOGLE_MAPS_KEY)"));
      return;
    }
    const mapId = process.env.REACT_APP_GOOGLE_MAPS_ID;
    const idParam = mapId ? `&map_ids=${mapId}` : "";
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}${idParam}&libraries=places,geometry,maps3d&v=weekly`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Google Maps"));
    document.head.appendChild(script);
  });
  return mapsLoadPromise;
}

export function useGoogleMaps() {
  const [loaded, setLoaded] = useState<boolean>(!!window.google?.maps);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loaded) return;
    loadGoogleMaps()
      .then(() => setLoaded(true))
      .catch((e: Error) => setError(e.message));
  }, [loaded]);

  return { loaded, error };
}
